import React, { useState, useRef, useEffect } from 'react';
import { Button } from './ui/button';
import { Card } from './ui/card';
import { FileText, RotateCcw, Loader2, Trash2, Eye, X, Calculator, ChevronDown, Share2, Check, Copy, Plus, ExternalLink } from 'lucide-react';
import useStore from '../store/useStore';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import { shareProject } from '../api';
import { isPamyatniki } from '../lib/variant';
import posthog from 'posthog-js';

export default function CostCalculator() {
  const {
    getTotalPrice,
    getTotalInstallationPrice,
    getProjectData,
    placedElements,
    selectedFloor,
    selectedFoundation,
    selectedFence,
    selectedBorder,
    perimeterWidth,
    perimeterLength,
    clearPlacedElements,
    setSelectedFloor,
    setSelectedFoundation,
    setSelectedFence,
    setSelectedBorder,
    selectedPlacedId,
    setSelectedPlacedId,
    removePlacedElement,
    isSharedView,
    setSharedView,
    resetCameraCallback,
    customItems,
    addCustomItem,
    removeCustomItem,
    categories,
    elements: catalogElements,
    flowerbedFilling
  } = useStore();
  
  const [isExporting, setIsExporting] = useState(false);
  const [selectedStaticId, setSelectedStaticId] = useState(null);
  const [isSharing, setIsSharing] = useState(false);
  const [shareLink, setShareLink] = useState(null);
  const [copied, setCopied] = useState(false);
  
  // Check if opening from shared link (URL has ?project=)
  const isFromSharedLink = typeof window !== 'undefined' && new URLSearchParams(window.location.search).has('project');
  // Calculator starts collapsed by default (shared link or fresh start)
  const [isCollapsed, setIsCollapsed] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [newItemName, setNewItemName] = useState('');
  const [newItemPrice, setNewItemPrice] = useState('');
  const [installationExpanded, setInstallationExpanded] = useState(false);
  const [excludedInstallItems, setExcludedInstallItems] = useState(new Set());
  const printRef = useRef(null);
  const calculatorRef = useRef(null);
  
  // Auto-collapse ТОЛЬКО при переходе desktop -> mobile (сужение окна за брейкпоинт).
  // Раньше слушатель схлопывал калькулятор на ЛЮБОМ resize, а на телефоне resize
  // стреляет при появлении/скрытии экранной клавиатуры (поле «Добавить пункт» с autoFocus),
  // из-за чего панель цены закрывалась сама и не давала открыться повторно.
  useEffect(() => {
    let wasDesktop = window.innerWidth >= 768;
    const handleResize = () => {
      const isNowMobile = window.innerWidth < 768;
      if (isNowMobile && wasDesktop) {
        setIsCollapsed(true);
      }
      wasDesktop = !isNowMobile;
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // При сворачивании калькулятора гасим модалку «Добавить пункт», чтобы при повторном
  // открытии панели не всплывал диалог вместо самого калькулятора.
  useEffect(() => {
    if (isCollapsed) {
      setShowAddModal(false);
      setNewItemName('');
      setNewItemPrice('');
    }
  }, [isCollapsed]);

  // Click outside to close (mobile only)
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (window.innerWidth >= 768) return;
      if (!isCollapsed && calculatorRef.current && !calculatorRef.current.contains(event.target)) {
        setIsCollapsed(true);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isCollapsed]);

  // Collapse if shared view changes
  useEffect(() => {
    if (isSharedView) {
      setIsCollapsed(true);
    }
  }, [isSharedView]);

  // Reset shared view when user opens calculator
  const handleOpenCalculator = () => {
    setIsCollapsed(false);
    if (isSharedView) {
      setSharedView(false);
    }
  };
  
  // Calculate perimeter length for fence
  const perimeterLengthMeters = 2 * (perimeterWidth + perimeterLength);
  const plotArea = perimeterWidth * perimeterLength;

  const getInstallationBreakdown = () => {
    const items = [];
    const getCat = (name) => categories.find(c => c.name === name);
    const getCatById = (id) => categories.find(c => c.id === id);

    if (selectedFoundation) {
      const cat = getCat('foundation');
      const price = (cat?.installation_price || 0) * plotArea;
      if (price > 0) items.push({ label: 'Фундамент', price });
    }
    if (selectedFloor) {
      const installPerM2 = selectedFloor.installation_price || 0;
      const price = installPerM2 * plotArea;
      if (price > 0) items.push({ label: 'Напольное покрытие (укладка)', price });
    }
    if (selectedBorder) {
      const cat = getCat('border');
      const price = (cat?.installation_price || 0) * perimeterLengthMeters;
      if (price > 0) items.push({ label: 'Бордюр', price });
    }
    if (selectedFence) {
      const cat = getCat('fence');
      const price = (cat?.installation_price || 0) * perimeterLengthMeters;
      if (price > 0) items.push({ label: 'Оградка', price });
    }

    // Group placed elements by category
    const groupedEls = {};
    placedElements.forEach(el => {
      const cat = getCatById(el.category_id);
      const installPrice = cat?.installation_price || 0;
      if (installPrice > 0) {
        const key = cat.id;
        if (!groupedEls[key]) groupedEls[key] = { label: cat.name_ru, count: 0, unitPrice: installPrice };
        groupedEls[key].count += 1;
      }
    });
    Object.values(groupedEls).forEach(({ label, count, unitPrice }) => {
      items.push({ label, count, price: unitPrice * count });
    });

    return items;
  };

  const getTotalFlowerbedFillingPrice = () => {
    // Наполнение цветника: Земля = 0, выбранное покрытие = цена/м² * площадь каждого цветника
    if (!flowerbedFilling) return 0;
    const fillPrice = Number(flowerbedFilling.price) || 0;
    return Math.round(fillPrice * placedElements
      // Наполнение считаем только для полого (П-образного) цветника.
      .filter(el => el.model_type === 'flowerbed' && el.flowerbed_shape != null && el.flowerbed_shape !== 'rectangle')
      .reduce((sum, el) => {
        const borderThick = 0.08;
        const innerW = Math.max(0, (el.width || 1) - 2 * borderThick);
        const innerD = Math.max(0, (el.depth || 1) - borderThick);
        return sum + innerW * innerD;
      }, 0));
  };

  const getBorderTotalPrice = (border) => {
    if (!border) return 0;
    const unit = border.price_unit || 'linear_meter';
    const basePrice = Number(border.price) || 0;
    if (unit === 'cubic_meter') {
      // Бордюр из камня: объём облицовки = периметр × высота × толщина (2см)
      const h = Number(border.height) || 0.1;
      const t = Number(border.pricing_thickness_m) || 0.02;
      return Math.round(basePrice * perimeterLengthMeters * h * t);
    }
    // Бетонный бордюр: цена за погонный метр
    return Math.round(basePrice * perimeterLengthMeters);
  };

  const getElementPrice = (el) => {
    const priceUnit = el.price_unit || 'piece';
    const basePrice = el.price || 0;
    // Pamyatniki: плоская доплата за форму (резка арки и т.п.) из поля «Цена установки»
    // элемента. У обычных стел = 0. Должна совпадать с надбавкой в useStore.getTotalPrice.
    const formSurcharge = isPamyatniki ? (el.installation_price || 0) : 0;
    if (priceUnit === 'cubic_meter') {
      const w = el.width || 1, h = el.height || 1, d = el.depth || 1;
      const isPShape = el.model_type === 'flowerbed' && el.flowerbed_shape != null && el.flowerbed_shape !== 'rectangle';
      const volume = isPShape ? 0.08 * h * (2 * d + w) : w * h * d;
      return Math.round(basePrice * volume) + formSurcharge;
    } else if (priceUnit === 'linear_meter') {
      return Math.round(basePrice * (el.width || 1));
    } else if (priceUnit === 'sqmeter') {
      return Math.round(basePrice * (el.width || 1) * (el.depth || 1));
    } else if (priceUnit === 'sqmeter_wh') {
      const origEl = catalogElements?.find(e => e.id === (el.element_id || el.id));
      const defaultArea = origEl ? (origEl.width || 1) * (origEl.height || 1) : 1;
      const currentArea = (el.width || 1) * (el.height || 1);
      return Math.round(basePrice * (currentArea / defaultArea));
    }
    return basePrice;
  };

  // Для PDF/таблицы: показываем "кол-во" в понятном виде (шт / м² / п.м. / м³)
  const formatQty = (value) => {
    const v = Number(value) || 0;
    // 2 знака после запятой, но без лишних нулей
    return v.toFixed(2).replace(/\.?0+$/, '');
  };

  const getElementQtyLabel = (el) => {
    const priceUnit = el.price_unit || 'piece';
    const w = el.width || 1;
    const h = el.height || 1;
    const d = el.depth || 1;

    if (priceUnit === 'cubic_meter') {
      const isPShape = el.model_type === 'flowerbed' && el.flowerbed_shape != null && el.flowerbed_shape !== 'rectangle';
      const volume = isPShape ? 0.08 * h * (2 * d + w) : w * h * d;
      return `${formatQty(volume)} м³`;
    }
    if (priceUnit === 'sqmeter') {
      const area = w * d;
      return `${formatQty(area)} м²`;
    }
    if (priceUnit === 'sqmeter_wh') {
      const area = w * h;
      return `${formatQty(area)} м²`;
    }
    if (priceUnit === 'linear_meter') {
      // В проекте длина хранится в width
      return `${formatQty(w)} п.м.`;
    }
    return '1 шт';
  };

  // Получаем подпись категории и мягкий цвет для маленькой метки
  const getCategoryInfo = (el) => {
    const cat = categories?.find(c => c.id === el.category_id);
    const name = cat?.name || '';
    const nameRu = cat?.name_ru || '';
    // Цвет используем только для метки категории, чтобы список оставался визуально чистым
    const colorMap = {
      monument: { badge: 'bg-rose-100 text-rose-700' },
      pedestal: { badge: 'bg-orange-100 text-orange-700' },
      flowerbed: { badge: 'bg-emerald-100 text-emerald-700' },
      bench: { badge: 'bg-cyan-100 text-cyan-700' },
      table: { badge: 'bg-indigo-100 text-indigo-700' },
      accessories: { badge: 'bg-violet-100 text-violet-700' },
    };
    const colors = colorMap[name] || { badge: 'bg-slate-100 text-slate-700' };
    return { nameRu, colors };
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('kk-KZ', {
      style: 'decimal',
      maximumFractionDigits: 0
    }).format(price) + ' ₸';
  };

  const handleShare = async () => {
    setIsSharing(true);
    try {
      // Capture screenshot for OG preview
      let screenshot = null;
      if (resetCameraCallback) {
        try {
          screenshot = await resetCameraCallback();
        } catch (e) {
          // Fallback to direct canvas capture
          const canvas = document.querySelector('[data-testid="3d-scene"] canvas');
          if (canvas) {
            screenshot = canvas.toDataURL('image/jpeg', 0.7);
          }
        }
      }
      
      const response = await shareProject({
        perimeter_width: perimeterWidth,
        perimeter_length: perimeterLength,
        placed_elements: placedElements,
        selected_floor: selectedFloor,
        selected_foundation: selectedFoundation,
        selected_fence: selectedFence,
        selected_border: selectedBorder,
        custom_items: customItems,
        total_price: getTotalPrice(),
        screenshot: screenshot
      });
      
      const link = `${window.location.origin}/?project=${response.data.id}`;
      setShareLink(link);
    } catch (error) {
      console.error('Share error:', error);
    } finally {
      setIsSharing(false);
    }
  };

  const handleCopyLink = async () => {
    if (!shareLink) return;
    
    try {
      // Try modern clipboard API first
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(shareLink);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
        return;
      }
    } catch (error) {
      console.error('Clipboard API error:', error);
    }
    
    // Fallback: create temp input and select
    try {
      const textArea = document.createElement('textarea');
      textArea.value = shareLink;
      textArea.style.cssText = 'position:fixed;left:-9999px;top:0;';
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();
      const success = document.execCommand('copy');
      document.body.removeChild(textArea);
      
      if (success) {
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
        return;
      }
    } catch (error) {
      console.error('execCommand error:', error);
    }
    
    // Last resort: prompt user to copy manually
    window.prompt('Скопируйте ссылку (Ctrl+C):', shareLink);
  };

  const handleNativeShare = async () => {
    if (!shareLink || !navigator.share) return;

    // Памятники: монтаж не включаем в итог (как в EmbedPriceChip).
    const totalPrice = getTotalPrice() - (isPamyatniki ? getTotalInstallationPrice() : 0);
    const formattedPrice = new Intl.NumberFormat('kk-KZ', {
      style: 'decimal',
      maximumFractionDigits: 0
    }).format(totalPrice) + ' ₸';
    
    try {
      await navigator.share({
        title: isPamyatniki ? 'iRitual - Эскиз памятника' : 'iRitual - Проект благоустройства',
        text: isPamyatniki
          ? `Посмотрите мой эскиз памятника.\nСтоимость: ${formattedPrice}`
          : `Посмотрите мой проект благоустройства места захоронения.\nСтоимость: ${formattedPrice}`,
        url: shareLink
      });
    } catch (error) {
      if (error.name !== 'AbortError') {
        console.error('Share error:', error);
      }
    }
  };

  const handleExportPDF = async () => {
    setIsExporting(true);
    
    try {
      const projectData = getProjectData();
      // Памятники: исключаем монтаж из итога в PDF.
      if (isPamyatniki) projectData.total_price -= getTotalInstallationPrice();

      // Capture scene screenshot - always horizontal
      let sceneScreenshot = null;
      
      // Try callback first (renders scene at optimal angle)
      if (resetCameraCallback) {
        try {
          const result = await resetCameraCallback();
          if (result && typeof result === 'string' && result.startsWith('data:')) {
            sceneScreenshot = result;
          }
        } catch (e) {
          console.log('Scene capture via callback failed:', e);
        }
      }
      
      // Fallback: capture and FILL horizontal canvas (crop if needed)
      if (!sceneScreenshot) {
        const sceneElement = document.querySelector('[data-testid="3d-scene"]');
        if (sceneElement) {
          const canvas = sceneElement.querySelector('canvas');
          if (canvas) {
            // Always create horizontal canvas (16:9)
            const targetWidth = 1280;
            const targetHeight = 720;
            const tempCanvas = document.createElement('canvas');
            tempCanvas.width = targetWidth;
            tempCanvas.height = targetHeight;
            const ctx = tempCanvas.getContext('2d');
            
            // Calculate how to FILL target (crop excess)
            const sourceAspect = canvas.width / canvas.height;
            const targetAspect = targetWidth / targetHeight;
            
            let sx, sy, sWidth, sHeight;
            
            if (sourceAspect > targetAspect) {
              // Source is wider - crop left/right
              sHeight = canvas.height;
              sWidth = canvas.height * targetAspect;
              sx = (canvas.width - sWidth) / 2;
              sy = 0;
            } else {
              // Source is taller (mobile) - crop top/bottom
              sWidth = canvas.width;
              sHeight = canvas.width / targetAspect;
              sx = 0;
              sy = (canvas.height - sHeight) / 2;
            }
            
            // Draw cropped and scaled to fill entire target
            ctx.drawImage(
              canvas,
              sx, sy, sWidth, sHeight,  // Source rectangle
              0, 0, targetWidth, targetHeight  // Destination (full canvas)
            );
            
            sceneScreenshot = tempCanvas.toDataURL('image/jpeg', 0.92);
          }
        }
      }
      
      // Create a temporary container for PDF content
      const container = document.createElement('div');
      container.style.cssText = `
        position: fixed;
        left: -9999px;
        top: 0;
        width: 800px;
        padding: 40px;
        background: white;
        font-family: Arial, sans-serif;
      `;
      
      const area = perimeterWidth * perimeterLength;
      
      // Find engraving text element (if any) for the header
      const engravingTextEl = placedElements.find(el => el.model_type === 'engraving_text');
      const deceasedName = engravingTextEl
        ? [engravingTextEl.lastName, engravingTextEl.firstName, engravingTextEl.patronymic].filter(Boolean).join(' ')
        : '';

      // Спецификация: собираем строки в массив со сквозной нумерацией.
      // Раньше строки писались инлайном, а № считался формулами — бордюр
      // при этом вообще не попадал в таблицу (был только в «Итого»).
      const specRows = [];
      if (projectData.foundation) {
        specRows.push({
          name: `Фундамент: ${projectData.foundation.name_ru}`,
          qty: `${area.toFixed(1)} м²`,
          price: projectData.foundation.price
        });
      }
      if (projectData.floor) {
        specRows.push({
          name: `Покрытие: ${projectData.floor.name_ru}`,
          qty: `${area.toFixed(1)} м²`,
          price: projectData.floor.price
        });
      }
      if (projectData.border) {
        specRows.push({
          name: `Бордюр: ${projectData.border.name_ru}`,
          qty: `${projectData.border.perimeter_length.toFixed(1)} п.м.`,
          price: projectData.border.price
        });
      }
      if (projectData.fence) {
        specRows.push({
          name: `Оградка: ${projectData.fence.name_ru}`,
          qty: `${projectData.fence.perimeter_length.toFixed(1)} п.м.`,
          price: projectData.fence.price
        });
      }
      projectData.elements.forEach(el => {
        const cat = categories?.find(c => c.id === el.category_id);
        const prefix = cat?.name_ru ? (cat.name_ru + ': ') : '';
        const variant = el.colorName ? (' (' + el.colorName + ')') : '';
        const fioLine = el.model_type === 'engraving_text'
          ? [el.lastName, el.firstName, el.patronymic].filter(Boolean).join(' ')
          : '';
        specRows.push({
          name: prefix + (el.name_ru || '') + variant
            + (fioLine ? `<br><span style="font-size:12px;color:#555;font-style:italic;">${fioLine}</span>` : ''),
          qty: getElementQtyLabel(el),
          price: getElementPrice(el)
        });
      });
      (projectData.custom_items || []).forEach(item => {
        specRows.push({
          name: item.name,
          qty: '-',
          price: item.price,
          rowStyle: ' style="background: #fffbeb;"',
          nameStyle: ' color: #b45309;'
        });
      });
      if (getTotalFlowerbedFillingPrice() > 0) {
        specRows.push({
          name: `Наполнение цветников (${flowerbedFilling?.name_ru || 'Покрытие'})`,
          qty: '-',
          price: getTotalFlowerbedFillingPrice()
        });
      }

      container.innerHTML = `
        <div style="text-align: center; margin-bottom: 30px;">
          <h1 style="font-size: 28px; color: #1a1a2e; margin: 0;">${isPamyatniki ? 'Эскиз памятника' : 'Проект благоустройства'}</h1>
          ${isPamyatniki ? '' : '<h2 style="font-size: 22px; color: #1a1a2e; margin: 5px 0;">места захоронения</h2>'}
          ${deceasedName ? `<p style="font-size: 18px; color: #333; margin: 10px 0 0 0; font-style: italic;">${deceasedName}</p>` : ''}
          <p style="color: #666; font-size: 14px; margin-top: 10px;">Дата создания: ${new Date().toLocaleDateString('ru-RU')}</p>
        </div>
        
        ${sceneScreenshot ? `
          <div style="margin-bottom: 30px; text-align: center;">
            <h3 style="font-size: 16px; color: #333; margin-bottom: 10px; text-align: left;">Визуализация проекта:</h3>
            <img src="${sceneScreenshot}" style="max-width: 100%; height: auto; max-height: 300px; border: 1px solid #ddd; border-radius: 8px;" />
          </div>
        ` : ''}
        
        <div style="margin-bottom: 25px; padding: 15px; background: #f8f9fa; border-radius: 8px;">
          <h3 style="font-size: 16px; color: #333; margin: 0 0 10px 0;">Параметры участка:</h3>
          <p style="margin: 5px 0; color: #555;">Размер: ${projectData.perimeter_width} × ${projectData.perimeter_length} м</p>
          <p style="margin: 5px 0; color: #555;">Площадь: ${area.toFixed(1)} м²</p>
        </div>
        
        <div style="margin-bottom: 25px;">
          <h3 style="font-size: 16px; color: #333; margin-bottom: 15px;">Спецификация элементов:</h3>
          <table style="width: 100%; border-collapse: collapse; font-size: 14px;">
            <thead>
              <tr style="background: #f0f0f0;">
                <th style="padding: 10px; text-align: left; border-bottom: 2px solid #ddd;">№</th>
                <th style="padding: 10px; text-align: left; border-bottom: 2px solid #ddd;">Наименование</th>
                <th style="padding: 10px; text-align: center; border-bottom: 2px solid #ddd;">Кол-во</th>
                <th style="padding: 10px; text-align: right; border-bottom: 2px solid #ddd;">Цена</th>
              </tr>
            </thead>
            <tbody>
              ${specRows.map((row, idx) => `
                <tr${row.rowStyle || ''}>
                  <td style="padding: 8px; border-bottom: 1px solid #eee;">${idx + 1}</td>
                  <td style="padding: 8px; border-bottom: 1px solid #eee;${row.nameStyle || ''}">${row.name}</td>
                  <td style="padding: 8px; text-align: center; border-bottom: 1px solid #eee;">${row.qty}</td>
                  <td style="padding: 8px; text-align: right; border-bottom: 1px solid #eee;">${formatPrice(row.price)}</td>
                </tr>
              `).join('')}
            </tbody>
          </table>
        </div>

        ${!isPamyatniki && getTotalInstallationPrice() > 0 ? (() => {
          const baseNum = specRows.length + 1;
          const breakdown = getInstallationBreakdown();
          return `
        <div style="margin-bottom: 25px;">
          <h3 style="font-size: 16px; color: #333; margin-bottom: 15px;">Монтажные работы:</h3>
          <table style="width: 100%; border-collapse: collapse; font-size: 14px; table-layout: fixed;">
            <colgroup>
              <col style="width: 40px;" />
              <col />
              <col style="width: 100px;" />
              <col style="width: 120px;" />
            </colgroup>
            <thead>
              <tr style="background: #f0f0f0;">
                <th style="padding: 10px; text-align: left; border-bottom: 2px solid #ddd;">№</th>
                <th style="padding: 10px; text-align: left; border-bottom: 2px solid #ddd;">Наименование</th>
                <th style="padding: 10px; text-align: center; border-bottom: 2px solid #ddd;">Кол-во</th>
                <th style="padding: 10px; text-align: right; border-bottom: 2px solid #ddd;">Цена</th>
              </tr>
            </thead>
            <tbody>
              ${breakdown.map((item, i) => `
                <tr>
                  <td style="padding: 8px; border-bottom: 1px solid #eee;">${baseNum + i}</td>
                  <td style="padding: 8px; border-bottom: 1px solid #eee;">${item.label}</td>
                  <td style="padding: 8px; text-align: center; border-bottom: 1px solid #eee;">${item.count ? `${item.count} шт` : '-'}</td>
                  <td style="padding: 8px; text-align: right; border-bottom: 1px solid #eee;">${formatPrice(item.price)}</td>
                </tr>
              `).join('')}
            </tbody>
          </table>
        </div>
          `;
        })() : ''}
        
        <div style="padding: 20px; background: linear-gradient(135deg, #1a1a2e 0%, #16213e 100%); border-radius: 8px; color: white; display: flex; justify-content: space-between; align-items: center;">
          <span style="font-size: 18px; font-weight: 500;">ИТОГО:</span>
          <span style="font-size: 28px; font-weight: bold;">${formatPrice(projectData.total_price)}</span>
        </div>
        
        <div style="margin-top: 20px; padding: 16px 20px; background: #f0f7ff; border: 1px solid #bfdbfe; border-radius: 8px;">
          <p style="margin: 0 0 10px 0; font-size: 13px; font-weight: 600; color: #1e40af;">ℹ️&nbsp; Цена действительна при следующих условиях:</p>
          <div style="display: flex; flex-direction: column; gap: 6px;">
            <div style="display: flex; align-items: center; gap: 8px; font-size: 13px; color: #1e3a8a;">
              <span style="color: #16a34a; font-weight: bold;">✓</span>
              Есть <strong>подъезд к месту захоронения</strong>
            </div>
            <div style="display: flex; align-items: center; gap: 8px; font-size: 13px; color: #1e3a8a;">
              <span style="color: #16a34a; font-weight: bold;">✓</span>
              В пределах <strong>города</strong>
            </div>
            <div style="display: flex; align-items: center; gap: 8px; font-size: 13px; color: #1e3a8a;">
              <span style="color: #16a34a; font-weight: bold;">✓</span>
              Участок <strong>ровный</strong>
            </div>
          </div>
        </div>

        <div style="margin-top: 20px; text-align: center; color: #999; font-size: 12px;">
          <p style="margin: 5px 0;">iRitual - ${isPamyatniki ? 'Конструктор памятников' : 'Конструктор благоустройства мест захоронения'}</p>
          <p style="margin: 5px 0;">Данный документ является предварительным расчётом стоимости</p>
        </div>
      `;
      
      document.body.appendChild(container);
      
      // Convert to canvas with html2canvas
      const canvas = await html2canvas(container, {
        scale: 2,
        useCORS: true,
        allowTaint: true,
        backgroundColor: '#ffffff'
      });
      
      document.body.removeChild(container);
      
      // Create PDF from canvas
      const imgData = canvas.toDataURL('image/jpeg', 0.95);
      const pdf = new jsPDF('p', 'mm', 'a4');
      
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = pdf.internal.pageSize.getHeight();
      
      // Calculate aspect ratio to fit content properly
      const canvasAspect = canvas.width / canvas.height;
      const pageAspect = pdfWidth / pdfHeight;
      
      let imgWidth, imgHeight, imgX, imgY;
      
      if (canvasAspect > pageAspect) {
        // Canvas is wider - fit to width
        imgWidth = pdfWidth;
        imgHeight = pdfWidth / canvasAspect;
        imgX = 0;
        imgY = 0;
      } else {
        // Canvas is taller - fit to height with margin
        imgHeight = pdfHeight;
        imgWidth = pdfHeight * canvasAspect;
        imgX = (pdfWidth - imgWidth) / 2;
        imgY = 0;
      }
      
      // Add image maintaining proportions
      pdf.addImage(imgData, 'JPEG', imgX, imgY, imgWidth, imgHeight);
      pdf.save(`iritual-project-${Date.now()}.pdf`);
      
    } catch (error) {
      console.error('PDF export error:', error);
    } finally {
      setIsExporting(false);
    }
  };

  const handleReset = () => {
    clearPlacedElements();
    setSelectedFloor(null);
    setSelectedFoundation(null);
    setSelectedFence(null);
    setSelectedBorder(null);
  };

  // Auto-trigger PDF/share when arriving from quiz with ?action=pdf|share.
  // Waits for catalog + quiz-applied scene before firing, then strips the
  // param so refresh doesn't retrigger.
  const actionTriggeredRef = useRef(false);
  useEffect(() => {
    if (actionTriggeredRef.current) return;
    if (typeof window === 'undefined') return;
    const params = new URLSearchParams(window.location.search);
    const action = params.get('action');
    if (action !== 'pdf' && action !== 'share') return;
    if (!categories.length || !catalogElements.length) return;
    const sceneReady = placedElements.length > 0
      || !!selectedFloor || !!selectedFoundation || !!selectedFence || !!selectedBorder;
    if (!sceneReady) return;

    actionTriggeredRef.current = true;
    setIsCollapsed(false);
    const url = new URL(window.location.href);
    url.searchParams.delete('action');
    window.history.replaceState({}, '', url);
    // 3D canvas needs a moment to render before we screenshot it for PDF.
    const delay = action === 'pdf' ? 1500 : 400;
    const timer = setTimeout(() => {
      if (action === 'pdf') handleExportPDF();
      else if (action === 'share') handleShare();
    }, delay);
    return () => clearTimeout(timer);
  }, [categories.length, catalogElements.length, placedElements.length,
      selectedFloor, selectedFoundation, selectedFence, selectedBorder]);

  const handleSelectElement = (id) => {
    setSelectedPlacedId(id);
  };

  const handleDeleteElement = (id) => {
    removePlacedElement(id);
    if (selectedPlacedId === id) {
      setSelectedPlacedId(null);
    }
  };

  const handleAddCustomItem = () => {
    if (newItemName.trim() && newItemPrice) {
      addCustomItem({
        name: newItemName.trim(),
        price: parseFloat(newItemPrice) || 0
      });
      setNewItemName('');
      setNewItemPrice('');
      setShowAddModal(false);
    }
  };

  const excludedInstallTotal = getInstallationBreakdown()
    .filter(it => excludedInstallItems.has(it.label))
    .reduce((s, it) => s + it.price, 0);
  // Памятники: монтаж исключаем целиком — в этом варианте установка не выделяется
  // в калькуляторе. Blago сохраняет старое поведение (минусуем только то, что юзер пометил).
  const installSubtract = isPamyatniki ? getTotalInstallationPrice() : excludedInstallTotal;
  const totalPrice = getTotalPrice() - installSubtract;
  const elementCount = placedElements.length + (selectedFloor ? 1 : 0) + (selectedFoundation ? 1 : 0) + (selectedBorder ? 1 : 0) + (selectedFence ? 1 : 0) + (customItems?.length || 0);

  const getProjectAnalyticsProps = () => {
    const placedByCategory = {};
    placedElements.forEach(el => {
      const cat = categories?.find(c => c.id === el.category_id);
      const key = cat?.name || 'unknown';
      placedByCategory[key] = (placedByCategory[key] || 0) + 1;
    });
    return {
      total_price: totalPrice,
      elements_count: elementCount,
      placed_elements_count: placedElements.length,
      custom_items_count: customItems?.length || 0,
      area_m2: plotArea,
      perimeter_m: perimeterLengthMeters,
      perimeter_width_m: perimeterWidth,
      perimeter_length_m: perimeterLength,
      has_foundation: !!selectedFoundation,
      has_floor: !!selectedFloor,
      has_fence: !!selectedFence,
      has_border: !!selectedBorder,
      monuments: placedByCategory.monument || 0,
      pedestals: placedByCategory.pedestal || 0,
      flowerbeds: placedByCategory.flowerbed || 0,
      benches: placedByCategory.bench || 0,
      tables: placedByCategory.table || 0,
      accessories: placedByCategory.accessories || 0,
      is_shared_view: !!isSharedView,
    };
  };

  // Collapsed state - just show total price with expand indicator
  if (isCollapsed) {
    return (
      <div className="pointer-events-auto absolute right-2 top-2 z-20">
        <Button
          onClick={handleOpenCalculator}
          className="h-12 px-3 rounded-xl shadow-lg bg-white border-2 border-slate-200 flex items-center gap-2 hover:bg-slate-100 hover:border-slate-300"
          variant="outline"
          data-testid="calculator-toggle"
        >
          <Calculator className="h-5 w-5 text-primary" />
          <span className="font-bold text-slate-700">{formatPrice(totalPrice)}</span>
          <ChevronDown className="h-4 w-4 text-slate-400" />
        </Button>
      </div>
    );
  }

  const isMobile = window.innerWidth < 768;
  const containerClassName = isMobile
    ? "pointer-events-auto fixed inset-0 bg-white z-50 w-full h-[100dvh] flex flex-col p-3"
    : "pointer-events-auto absolute right-0 top-0 bg-white border-l border-slate-200 shadow-2xl p-3 z-20 w-80 max-h-[calc(100dvh-120px)] rounded-bl-2xl flex flex-col overflow-y-auto";

  return (
    <div 
      ref={calculatorRef}
      className={containerClassName}
      data-testid="cost-calculator"
    >
      {/* Header with close button */}
      <div className="flex items-center justify-between mb-3">
        <div className="leading-tight">
          <h3 className="text-sm font-medium text-slate-700">Калькулятор стоимости</h3>
          <div className="text-[11px] text-slate-400">Предварительная стоимость</div>
        </div>
        <Button
          onClick={() => setIsCollapsed(true)}
          className="h-8 w-8 rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-600"
          variant="ghost"
          data-testid="calculator-collapse"
        >
          <X className="h-4 w-4" />
        </Button>
      </div>
      
      {/* Summary */}
      <div className="grid grid-cols-3 gap-2 mb-3 text-center">
        <div className="bg-slate-50 rounded-lg p-2">
          <div className="text-[10px] md:text-xs text-slate-500">Элементов</div>
          <div className="text-sm font-medium">{elementCount}</div>
        </div>
        <div className="bg-slate-50 rounded-lg p-2">
          <div className="text-[10px] md:text-xs text-slate-500">Площадь</div>
          <div className="text-sm font-medium">{(perimeterWidth * perimeterLength).toFixed(1)} м²</div>
        </div>
        <div className="bg-slate-50 rounded-lg p-2">
          <div className="text-[10px] md:text-xs text-slate-500">Периметр</div>
          <div className="text-sm font-medium">{perimeterLengthMeters.toFixed(1)} м</div>
        </div>
      </div>

      {/* Список позиций проекта */}
      {elementCount > 0 && (
        <div className="space-y-1.5 mb-3 max-h-32 md:max-h-none overflow-y-auto custom-scrollbar">
          {/* Foundation (Layer 1) */}
          {selectedFoundation && (
            <div className={`flex items-center justify-between rounded-xl p-2.5 border transition-colors ${selectedStaticId === 'foundation' ? 'bg-primary/[0.06] border-primary/40 shadow-[0_1px_2px_rgba(15,23,42,0.08)]' : 'bg-white border-slate-100 hover:bg-slate-50/70'}`}>
              <div className="flex-1 min-w-0">
                <span className="inline-flex items-center rounded px-1.5 py-0.5 text-[10px] font-semibold bg-amber-100 text-amber-700">Слой 1</span>
                <span className="text-slate-700 ml-1.5 truncate text-xs md:text-sm">{selectedFoundation.name_ru}</span>
              </div>
              <span className="text-slate-900 mx-2 whitespace-nowrap text-xs md:text-sm font-semibold">
                {formatPrice(selectedFoundation.price * perimeterWidth * perimeterLength)}
              </span>
              <div className="flex gap-0.5">
                <Button
                  variant="ghost"
                  size="icon"
                  className={`h-6 w-6 ${selectedStaticId === 'foundation' ? 'text-primary' : 'text-slate-300 hover:text-primary'}`}
                  onClick={() => setSelectedStaticId(selectedStaticId === 'foundation' ? null : 'foundation')}
                  title="Выбрать"
                >
                  <Eye className="h-3 w-3" />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-6 w-6 text-slate-300 hover:text-destructive"
                  onClick={() => { setSelectedFoundation(null); setSelectedStaticId(null); }}
                >
                  <Trash2 className="h-3 w-3" />
                </Button>
              </div>
            </div>
          )}
          
          {/* Floor (Layer 2) */}
          {selectedFloor && (
            <div className={`flex items-center justify-between rounded-xl p-2.5 border transition-colors ${selectedStaticId === 'floor' ? 'bg-primary/[0.06] border-primary/40 shadow-[0_1px_2px_rgba(15,23,42,0.08)]' : 'bg-white border-slate-100 hover:bg-slate-50/70'}`}>
              <div className="flex-1 min-w-0">
                <span className="inline-flex items-center rounded px-1.5 py-0.5 text-[10px] font-semibold bg-blue-100 text-blue-700">Слой 2</span>
                <span className="text-slate-700 ml-1.5 truncate text-xs md:text-sm">{selectedFloor.name_ru}</span>
              </div>
              <span className="text-slate-900 mx-2 whitespace-nowrap text-xs md:text-sm font-semibold">
                {formatPrice(selectedFloor.price * perimeterWidth * perimeterLength)}
              </span>
              <div className="flex gap-0.5">
                <Button
                  variant="ghost"
                  size="icon"
                  className={`h-6 w-6 ${selectedStaticId === 'floor' ? 'text-primary' : 'text-slate-300 hover:text-primary'}`}
                  onClick={() => setSelectedStaticId(selectedStaticId === 'floor' ? null : 'floor')}
                  title="Выбрать"
                >
                  <Eye className="h-3 w-3" />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-6 w-6 text-slate-300 hover:text-destructive"
                  onClick={() => { setSelectedFloor(null); setSelectedStaticId(null); }}
                >
                  <Trash2 className="h-3 w-3" />
                </Button>
              </div>
            </div>
          )}
          
          {/* Border (perimeter-based) */}
          {selectedBorder && (
            <div className={`flex items-center justify-between rounded-xl p-2.5 border transition-colors ${selectedStaticId === 'border' ? 'bg-primary/[0.06] border-primary/40 shadow-[0_1px_2px_rgba(15,23,42,0.08)]' : 'bg-white border-slate-100 hover:bg-slate-50/70'}`}>
              <div className="flex-1 min-w-0">
                <span className="inline-flex items-center rounded px-1.5 py-0.5 text-[10px] font-semibold bg-purple-100 text-purple-700">Бордюр</span>
                <span className="text-slate-700 ml-1.5 truncate text-xs md:text-sm">{selectedBorder.name_ru}</span>
              </div>
              <span className="text-slate-900 mx-2 whitespace-nowrap text-xs md:text-sm font-semibold">
                {formatPrice(getBorderTotalPrice(selectedBorder))}
              </span>
              <div className="flex gap-0.5">
                <Button
                  variant="ghost"
                  size="icon"
                  className={`h-6 w-6 ${selectedStaticId === 'border' ? 'text-primary' : 'text-slate-300 hover:text-primary'}`}
                  onClick={() => setSelectedStaticId(selectedStaticId === 'border' ? null : 'border')}
                  title="Выбрать"
                >
                  <Eye className="h-3 w-3" />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-6 w-6 text-slate-300 hover:text-destructive"
                  onClick={() => { setSelectedBorder(null); setSelectedStaticId(null); }}
                >
                  <Trash2 className="h-3 w-3" />
                </Button>
              </div>
            </div>
          )}
          
          {/* Fence (perimeter-based) */}
          {selectedFence && (
            <div className={`flex items-center justify-between rounded-xl p-2.5 border transition-colors ${selectedStaticId === 'fence' ? 'bg-primary/[0.06] border-primary/40 shadow-[0_1px_2px_rgba(15,23,42,0.08)]' : 'bg-white border-slate-100 hover:bg-slate-50/70'}`}>
              <div className="flex-1 min-w-0">
                <span className="inline-flex items-center rounded px-1.5 py-0.5 text-[10px] font-semibold bg-green-100 text-green-700">Оградка</span>
                <span className="text-slate-700 ml-1.5 truncate text-xs md:text-sm">{selectedFence.name_ru}</span>
              </div>
              <span className="text-slate-900 mx-2 whitespace-nowrap text-xs md:text-sm font-semibold">
                {formatPrice(selectedFence.price * perimeterLengthMeters)}
              </span>
              <div className="flex gap-0.5">
                <Button
                  variant="ghost"
                  size="icon"
                  className={`h-6 w-6 ${selectedStaticId === 'fence' ? 'text-primary' : 'text-slate-300 hover:text-primary'}`}
                  onClick={() => setSelectedStaticId(selectedStaticId === 'fence' ? null : 'fence')}
                  title="Выбрать"
                >
                  <Eye className="h-3 w-3" />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-6 w-6 text-slate-300 hover:text-destructive"
                  onClick={() => { setSelectedFence(null); setSelectedStaticId(null); }}
                >
                  <Trash2 className="h-3 w-3" />
                </Button>
              </div>
            </div>
          )}
          
          {/* Наполнение цветников (отдельной строкой в списке) */}
          {flowerbedFilling && placedElements.some(el => el.model_type === 'flowerbed' && el.flowerbed_shape != null) && (
            <div className="flex items-center justify-between rounded-xl p-2.5 border border-slate-100 bg-white shadow-[0_1px_2px_rgba(15,23,42,0.05)]">
              <div className="flex-1 min-w-0">
                <span className="inline-flex items-center rounded px-1.5 py-0.5 text-[10px] font-semibold bg-green-100 text-green-700 mr-1.5">Наполн.</span>
                <span className="text-slate-700 truncate text-xs md:text-sm">Наполнение цветников ({flowerbedFilling.name_ru})</span>
              </div>
              <span className="text-slate-900 mx-2 whitespace-nowrap text-xs md:text-sm font-semibold">
                {formatPrice(getTotalFlowerbedFillingPrice())}
              </span>
            </div>
          )}

          {/* Placed elements (Layer 3) */}
          {placedElements.map((el) => {
            const { nameRu: catName, colors } = getCategoryInfo(el);
            return (
            <div
              key={el.id}
              className={`flex items-center justify-between rounded-xl p-2.5 border transition-colors ${
                selectedPlacedId === el.id
                  ? 'bg-primary/[0.06] border-primary/40 shadow-[0_1px_2px_rgba(15,23,42,0.08)]'
                  : 'bg-white border-slate-100 hover:bg-slate-50/70'
              }`}
            >
              <div className="flex-1 min-w-0">
                {catName && <span className={`inline-flex items-center rounded px-1.5 py-0.5 text-[10px] font-semibold mr-1.5 ${colors.badge}`}>{catName}</span>}
                <span className="text-slate-700 truncate text-xs md:text-sm">{el.name_ru}</span>
              </div>
              <span className="text-slate-900 mx-2 whitespace-nowrap text-xs md:text-sm font-semibold">{formatPrice(getElementPrice(el))}</span>
              <div className="flex gap-0.5">
                <Button
                  variant="ghost"
                  size="icon"
                  className={`h-6 w-6 ${selectedPlacedId === el.id ? 'text-primary' : 'text-slate-300 hover:text-primary'}`}
                  onClick={() => handleSelectElement(el.id)}
                  title="Выбрать"
                >
                  <Eye className="h-3 w-3" />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-6 w-6 text-slate-300 hover:text-destructive"
                  onClick={() => handleDeleteElement(el.id)}
                  title="Удалить"
                >
                  <Trash2 className="h-3 w-3" />
                </Button>
              </div>
            </div>
          );
          })}

          {/* Custom items (delivery, installation, etc.) */}
          {customItems?.map((item) => (
            <div 
              key={item.id} 
              className="flex items-center justify-between rounded-xl p-2.5 border border-slate-100 bg-white shadow-[0_1px_2px_rgba(15,23,42,0.05)]"
            >
              <div className="flex-1 min-w-0">
                <span className="inline-flex items-center rounded px-1.5 py-0.5 text-[10px] font-semibold bg-amber-100 text-amber-700 mr-1.5">Доп.</span>
                <span className="text-slate-700 truncate text-xs md:text-sm">{item.name}</span>
              </div>
              <span className="text-slate-900 mx-2 whitespace-nowrap text-xs md:text-sm font-semibold">{formatPrice(item.price)}</span>
              <Button
                variant="ghost"
                size="icon"
                className="h-6 w-6 text-slate-300 hover:text-destructive"
                onClick={() => removeCustomItem(item.id)}
                title="Удалить"
              >
                <Trash2 className="h-3 w-3" />
              </Button>
            </div>
          ))}
          
          {/* Installation row — always last before add button. Памятники скрывают. */}
          {!isPamyatniki && getTotalInstallationPrice() > 0 && (
            <div className="border-t-2 border-slate-200 pt-1.5 mt-0.5">
              <div className="rounded-xl border border-slate-100 bg-white shadow-[0_1px_2px_rgba(15,23,42,0.05)] overflow-hidden">
                <button
                  className="w-full flex items-center justify-between p-2.5 hover:bg-slate-50 transition-colors"
                  onClick={() => setInstallationExpanded(v => !v)}
                >
                  <div className="flex-1 min-w-0 flex items-center">
                    <span className="inline-flex items-center rounded px-1.5 py-0.5 text-[10px] font-semibold bg-blue-100 text-blue-700 mr-1.5">Монтаж</span>
                    <span className="text-slate-700 truncate text-xs md:text-sm">Монтажные работы</span>
                  </div>
                  <span className="text-slate-900 mx-2 whitespace-nowrap text-xs md:text-sm font-semibold">
                    {formatPrice(getInstallationBreakdown().filter(it => !excludedInstallItems.has(it.label)).reduce((s, it) => s + it.price, 0))}
                  </span>
                  <span className="text-xs text-primary font-medium flex items-center gap-0.5 whitespace-nowrap">
                    {installationExpanded ? 'Скрыть' : 'Подробнее'}
                    <ChevronDown className={`h-3.5 w-3.5 transition-transform ${installationExpanded ? 'rotate-180' : ''}`} />
                  </span>
                </button>
                {installationExpanded && (
                  <div className="border-t border-slate-100 px-2.5 py-2 space-y-1">
                    {getInstallationBreakdown().map((item, i) => (
                      <div key={i} className={`flex items-center justify-between text-xs ${excludedInstallItems.has(item.label) ? 'opacity-40 line-through' : 'text-slate-600'}`}>
                        <span className="truncate">{item.label}{item.count > 1 ? ` × ${item.count}` : ''}</span>
                        <div className="flex items-center gap-1.5 ml-2">
                          <span className="whitespace-nowrap text-slate-700 font-medium">{formatPrice(item.price)}</span>
                          <button
                            onClick={() => setExcludedInstallItems(prev => {
                              const next = new Set(prev);
                              if (next.has(item.label)) next.delete(item.label); else next.add(item.label);
                              return next;
                            })}
                            className="text-slate-400 hover:text-red-500 transition-colors"
                            title={excludedInstallItems.has(item.label) ? 'Вернуть' : 'Убрать'}
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Add custom item button */}
          <Button
            variant="ghost"
            className="w-full h-8 text-xs text-slate-500 hover:text-primary hover:bg-primary/5 border border-dashed border-slate-300 hover:border-primary"
            onClick={() => setShowAddModal(true)}
            data-testid="add-custom-item-btn"
          >
            <Plus className="h-3.5 w-3.5 mr-1" />
            Добавить пункт
          </Button>
        </div>
      )}

      {/* Главный итог — самый заметный акцент */}
      <Card className="p-2.5 md:p-3 bg-gradient-to-r from-slate-800 to-slate-700 text-white mb-3 rounded-xl shadow-lg">
        <div className="flex justify-between items-center">
          <span className="text-xs md:text-sm text-slate-200">Итого:</span>
          <span className="text-lg md:text-xl font-bold" data-testid="total-price">
            {formatPrice(totalPrice)}
          </span>
        </div>
      </Card>

      {/* WhatsApp consultation button */}
      <a
        href={`https://wa.me/77052222869?text=${encodeURIComponent(`Здравствуйте, хочу узнать детали подробнее.\n\nМой проект: ${shareLink || window.location.href}\nСтоимость: ${formatPrice(totalPrice)}`)}`}
        target="_blank"
        rel="noopener noreferrer"
        onClick={() => posthog.capture('consultation_requested', { ...getProjectAnalyticsProps(), has_share_link: !!shareLink })}
        className="flex items-center justify-center gap-2 w-full h-10 mb-3 rounded-lg bg-[#25D366] hover:bg-[#20bd5a] text-white font-medium text-sm transition-colors"
        data-testid="whatsapp-btn"
      >
        <svg className="h-5 w-5" viewBox="0 0 24 24" fill="currentColor">
          <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
        </svg>
        Получить консультацию
      </a>

      {/* Actions */}
      <div className="flex gap-2">
        <Button
          className="flex-1 bg-accent hover:bg-accent/90 text-white text-xs md:text-sm h-9 md:h-10"
          onClick={() => {
            posthog.capture('pdf_exported', getProjectAnalyticsProps());
            handleExportPDF();
          }}
          disabled={elementCount === 0 || isExporting}
          data-testid="export-pdf-btn"
        >
          {isExporting ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <>
              <FileText className="h-4 w-4 mr-2" />
              Экспорт PDF
            </>
          )}
        </Button>
        <Button
          variant="outline"
          className="flex-1 text-xs md:text-sm h-9 md:h-10"
          onClick={() => {
            posthog.capture('project_reset', getProjectAnalyticsProps());
            handleReset();
          }}
          disabled={elementCount === 0}
          data-testid="reset-btn"
        >
          <RotateCcw className="h-4 w-4 mr-1 md:mr-2" />
          Сброс
        </Button>
      </div>

      {/* Share button */}
      <div className="mt-2">
        {!shareLink ? (
          <Button
            variant="outline"
            className="w-full text-xs md:text-sm h-9 md:h-10 border-primary/30 text-primary hover:bg-primary/5"
            onClick={() => {
              posthog.capture('project_shared', getProjectAnalyticsProps());
              handleShare();
            }}
            disabled={elementCount === 0 || isSharing}
            data-testid="share-btn"
          >
            {isSharing ? (
              <Loader2 className="h-4 w-4 animate-spin mr-2" />
            ) : (
              <Share2 className="h-4 w-4 mr-2" />
            )}
            Поделиться проектом
          </Button>
        ) : (
          <div className="space-y-2">
            <div className="flex items-center gap-2 p-2 bg-green-50 border border-green-200 rounded-lg">
              <Check className="h-4 w-4 text-green-600 flex-shrink-0" />
              <input 
                type="text"
                readOnly
                value={shareLink}
                onClick={(e) => e.target.select()}
                className="text-xs text-green-700 flex-1 bg-transparent border-none outline-none cursor-text"
                data-testid="share-link-input"
              />
              <Button
                size="icon"
                variant="ghost"
                className="h-7 w-7 flex-shrink-0"
                onClick={handleCopyLink}
                data-testid="copy-link-btn"
                title="Копировать ссылку"
              >
                {copied ? (
                  <Check className="h-3.5 w-3.5 text-green-600" />
                ) : (
                  <Copy className="h-3.5 w-3.5 text-slate-500" />
                )}
              </Button>
              {navigator.share && (
                <Button
                  size="icon"
                  variant="ghost"
                  className="h-7 w-7 flex-shrink-0"
                  onClick={handleNativeShare}
                  data-testid="native-share-btn"
                  title="Поделиться"
                >
                  <ExternalLink className="h-3.5 w-3.5 text-slate-500" />
                </Button>
              )}
            </div>
            <Button
              variant="ghost"
              size="sm"
              className="w-full text-xs text-slate-500"
              onClick={() => setShareLink(null)}
            >
              Создать новую ссылку
            </Button>
          </div>
        )}
      </div>
      
      {/* Add Custom Item Modal */}
      {showAddModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={() => setShowAddModal(false)}>
          <div 
            className="bg-white rounded-xl p-4 w-80 shadow-2xl mx-4"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="text-lg font-semibold text-slate-800 mb-4">Добавить пункт</h3>
            
            <div className="space-y-3">
              <div>
                <label className="block text-sm text-slate-600 mb-1">Наименование</label>
                <input
                  type="text"
                  value={newItemName}
                  onChange={(e) => setNewItemName(e.target.value)}
                  placeholder="Например: Доставка"
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary/50"
                  autoFocus
                  data-testid="custom-item-name-input"
                />
              </div>
              
              <div>
                <label className="block text-sm text-slate-600 mb-1">Сумма (₸)</label>
                <input
                  type="number"
                  value={newItemPrice}
                  onChange={(e) => setNewItemPrice(e.target.value)}
                  placeholder="5000"
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary/50"
                  data-testid="custom-item-price-input"
                />
              </div>
            </div>
            
            <div className="flex gap-2 mt-4">
              <Button
                variant="outline"
                className="flex-1"
                onClick={() => setShowAddModal(false)}
              >
                Отмена
              </Button>
              <Button
                className="flex-1 bg-primary hover:bg-primary/90"
                onClick={handleAddCustomItem}
                disabled={!newItemName.trim() || !newItemPrice}
                data-testid="add-custom-item-submit"
              >
                Добавить
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
